import React, { type ReactNode } from 'react';
import {
  Item,
  Label,
  List,
  Root,
  Segment,
  Separator,
} from './Breadcrumb.styled';

/**
 * State passed into `BreadcrumbSegment.render` so custom markup can
 * mirror the a11y surface the default `<Segment>` button stamps —
 * `aria-current="page"` on the current segment, `tabIndex` matching
 * interactivity, and the standard `data-*` hooks for selectors.
 */
export interface BreadcrumbRenderState {
  /** True when this segment is the breadcrumb's current page. */
  isCurrent: boolean;
  /** True when the segment carries an `onClick`. */
  isInteractive: boolean;
  /** `data-testid` the default segment would have used. */
  dataTestId: string;
  /** `aria-current` value — `"page"` on the current segment, else undefined. */
  ariaCurrent: 'page' | undefined;
  /** Suggested tabIndex — 0 for interactive segments, -1 otherwise. */
  tabIndex: number;
}

export interface BreadcrumbSegment {
  /** Stable id (used as the React key when rendering the list). */
  id: string;
  /** The visible label — usually a string, but accepts richer nodes. */
  label: ReactNode;
  /**
   * Click handler. When set, the segment becomes interactive (focusable
   * button with hover affordance). Mutually OK with {@link href}: pass
   * both if you want the link to behave like a button (e.g. for a router
   * `Link` consumer that calls `e.preventDefault()` and navigates
   * programmatically).
   */
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  /**
   * Trailing slot inside the segment — typically an `MdArrowDropDown`
   * chevron that hints at a dropdown menu. The molecule does NOT manage
   * the dropdown itself; pair this with `onClick` and an external menu
   * (e.g. `ChipMenu`) anchored to the segment.
   */
  trailing?: ReactNode;
  /**
   * Mark the segment as the current page in the breadcrumb trail.
   * Stamped with `aria-current="page"` for screen readers; visually it
   * gets a heavier weight + the surface foreground colour. Defaults to
   * `true` for the last segment if you don't set it explicitly on any
   * segment.
   */
  current?: boolean;
  /**
   * Escape hatch — render the segment body yourself instead of the
   * default button. When set, the molecule emits the surrounding
   * `<Item>` (so the separator weaving still works) but lets the
   * consumer drop arbitrary content into that slot. Used by the
   * process editor breadcrumb for the inline rename input on the
   * trailing draft segment; nested interactive elements (input inside
   * a button) would otherwise break a11y.
   *
   * When `render` is set, the molecule ignores {@link onClick},
   * {@link trailing}, and {@link label} for that segment — the
   * consumer owns the entire rendered output.
   *
   * The molecule passes the computed segment state (current /
   * interactive flags, dataTestId fallback) into the render fn so the
   * custom markup can stamp `aria-current="page"`, `data-current`,
   * `data-interactive`, and an appropriate `tabIndex` to match the
   * default `<Segment>` button's a11y surface.
   */
  render?: (state: BreadcrumbRenderState) => ReactNode;
  /**
   * Per-segment accessible label override — stamped onto the rendered
   * `<button>` as `aria-label`. Use when the visible label alone isn't
   * enough context for assistive tech (e.g. `"Rename process: Tetra"`
   * on the editable current segment of the process editor crumb).
   */
  ariaLabel?: string;
  /**
   * Per-segment `data-testid` override. When omitted, segments fall
   * back to the breadcrumb's `<root>__segment` testid (duplicated
   * across segments, so consumers query with `getAllByTestId`). Use
   * this to give a specific segment its own stable testid hook (e.g.
   * `breadcrumb__back`, `breadcrumb__current`).
   */
  dataTestId?: string;
}

export interface BreadcrumbProps {
  segments: BreadcrumbSegment[];
  /**
   * Visual separator between segments. Defaults to a slim chevron glyph;
   * pass any ReactNode (`<MdChevronRight />`, `'/'`, `'›'`) to override.
   */
  separator?: ReactNode;
  /**
   * Required for screen readers — breadcrumbs need an accessible name.
   * Defaults to `"Breadcrumb"`.
   */
  ariaLabel?: string;
  className?: string;
  dataTestId?: string;
}

const DefaultSeparator: React.FC = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path
      d="M9 6l6 6-6 6"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/**
 * Generic segment list (`Foo › Bar › Baz`). Each segment can be a static
 * label, a clickable button, or a button with a trailing chevron that
 * cues an external dropdown menu (mirrors the design's
 * `Processes › <name> ▾` editor breadcrumb).
 *
 * The `current` segment is auto-detected as the last one if no segment
 * explicitly opts in. Only that segment carries `aria-current="page"`.
 *
 * @example
 *   <Breadcrumb
 *     ariaLabel="Process editor"
 *     segments={[
 *       { id: 'home', label: 'Processes', onClick: () => navigate('/processes') },
 *       {
 *         id: 'process',
 *         label: 'Tetra-bracket',
 *         onClick: openProcessMenu,
 *         trailing: <MdArrowDropDown />,
 *         current: true,
 *       },
 *     ]}
 *   />
 */
const Breadcrumb: React.FC<BreadcrumbProps> = ({
  segments,
  separator,
  ariaLabel = 'Breadcrumb',
  className,
  dataTestId = 'breadcrumb',
}) => {
  const lastIndex = segments.length - 1;
  // If no segment is explicitly current, the last one wins. This matches
  // the convention every breadcrumb implementation in the wild follows.
  // If the consumer passes `current: true` on multiple segments we honour
  // ONLY the first — `aria-current="page"` is a singleton attribute and
  // multiple matches would confuse assistive tech.
  const firstExplicitCurrent = segments.findIndex((s) => s.current);
  const sep = separator ?? <DefaultSeparator />;

  return (
    <Root
      aria-label={ariaLabel}
      className={className}
      data-testid={dataTestId}
    >
      <List>
        {segments.map((segment, idx) => {
          const isCurrent =
            firstExplicitCurrent >= 0
              ? idx === firstExplicitCurrent
              : idx === lastIndex;
          const isInteractive = !!segment.onClick;
          const showSeparatorAfter = idx < lastIndex;

          const segmentTestId =
            segment.dataTestId ?? `${dataTestId}__segment`;
          const renderState: BreadcrumbRenderState = {
            isCurrent,
            isInteractive,
            dataTestId: segmentTestId,
            ariaCurrent: isCurrent ? 'page' : undefined,
            tabIndex: isInteractive ? 0 : -1,
          };

          return (
            <React.Fragment key={segment.id}>
              <Item data-testid={`${dataTestId}__item`}>
                {segment.render ? (
                  segment.render(renderState)
                ) : (
                  <Segment
                    $current={isCurrent}
                    $interactive={isInteractive}
                    onClick={segment.onClick}
                    aria-current={isCurrent ? 'page' : undefined}
                    aria-label={segment.ariaLabel}
                    // Non-interactive segments still render as <button> for
                    // markup consistency, but stripping `type` (via the
                    // .attrs above) and tabIndex=-1 keeps them out of the
                    // tab order.
                    tabIndex={isInteractive ? 0 : -1}
                    data-testid={segmentTestId}
                    data-current={isCurrent || undefined}
                    data-interactive={isInteractive || undefined}
                  >
                    <Label>{segment.label}</Label>
                    {segment.trailing && (
                      <span
                        className="seg-trailing"
                        aria-hidden="true"
                        data-testid={`${dataTestId}__segment-trailing`}
                      >
                        {segment.trailing}
                      </span>
                    )}
                  </Segment>
                )}
              </Item>
              {showSeparatorAfter && (
                <Separator
                  aria-hidden="true"
                  data-testid={`${dataTestId}__separator`}
                >
                  {sep}
                </Separator>
              )}
            </React.Fragment>
          );
        })}
      </List>
    </Root>
  );
};

export default Breadcrumb;
